<template>
  <canvas
    ref="el"
    class="avatar-view"
    :class="{ circle: clipCircle }"
    :width="pixel"
    :height="pixel"
    :style="{ width: cssSize, height: cssSize }"
    :aria-label="label || undefined"
    :aria-hidden="label ? undefined : 'true'"
  />
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { parseAvatarConfig, type AvatarConfig } from '@shared/index'
import { composeAvatar, type AvatarCrop } from '../avatar/composeAvatar'
import { isImageFrame } from '../avatar/drawFrame'

const props = withDefaults(
  defineProps<{
    config: AvatarConfig
    crop?: AvatarCrop
    size?: number
    circle?: boolean
    label?: string
  }>(),
  {
    crop: 'full',
    size: 160,
    circle: false,
    label: '',
  },
)

const el = ref<HTMLCanvasElement | null>(null)
let seq = 0

const pixel = computed(() => Math.max(24, Math.round(props.size)))
const cssSize = computed(() => `${props.size}px`)
const clipCircle = computed(
  () =>
    props.circle &&
    !isImageFrame(parseAvatarConfig(props.config).frameId),
)

async function paint(): Promise<void> {
  const canvas = el.value
  if (!canvas) return
  const token = ++seq
  const cfg = parseAvatarConfig(props.config)
  const drawn = await composeAvatar(cfg, {
    crop: props.crop,
    width: pixel.value,
    height: pixel.value,
    circle: props.circle,
  })
  if (token !== seq || !el.value) return
  const ctx = el.value.getContext('2d')
  if (!ctx) return
  ctx.clearRect(0, 0, el.value.width, el.value.height)
  ctx.drawImage(drawn, 0, 0)
}

onMounted(() => {
  void paint()
})

watch(
  () => [
    parseAvatarConfig(props.config).cardId,
    parseAvatarConfig(props.config).frameId,
    parseAvatarConfig(props.config).rev ?? 0,
    props.crop,
    props.size,
    props.circle,
  ],
  () => {
    void paint()
  },
)
</script>

<style scoped>
.avatar-view {
  display: block;
  flex-shrink: 0;
  max-width: 100%;
  height: auto;
}

.avatar-view.circle {
  border-radius: 50%;
  background: var(--md-sys-color-surface-container-high, #2a2430);
}
</style>
